import type { Component } from 'vue';
import { atsApi, timeApi, apprenticeApi } from './api';

// A static launcher link for modules with no live dashboard (People, Admin).
export interface QuickLink {
  key: string;
  label: string;
  icon?: Component;
  deepLink?: string;
}

// Shape of the module-owned `GET user-dashboard` envelope (workspace-128). The
// module curates its own key-facts/recents/quick-actions; the workspace only
// lays them out. `clock` is Zeit-only.
export interface KeyFact {
  key: string;
  label: string;
  value: number | string;
  deepLink?: string;
}
export interface RecentItem {
  key: string;
  label: string;
  deepLink: string;
  at?: string;
  // What the entry is (issue 995): the "Zuletzt" chip names it, so a report
  // week, an application and a person never read as the same bare name.
  kind?: 'report' | 'job' | 'application';
}
export interface QuickAction {
  key: string;
  label: string;
  icon: string;
  deepLink: string;
}
/**
 * A part-to-whole breakdown a module publishes, drawn as ONE stacked bar under
 * the key facts (#403). ATS sends its live hiring pipeline, Azubi its cohort of
 * report books; the workspace owns the bar and neither module owns a chart.
 *
 * `segments[].label` follows the key-fact rule (see `localizedLabel`): a stable
 * machine key the workspace localises when it knows it, and the module's English
 * default when it does not. That fallback is load-bearing here rather than
 * defensive — ATS names its segments after the TENANT's own board columns, which
 * the workspace can never have a translation for, so a workflow built in German
 * renders in German whatever language the reader picked.
 */
export interface BreakdownSegment {
  key: string;
  label: string;
  value: number;
  /**
   * `stage` walks the module's own accent ramp in order — a funnel. The others
   * are fixed semantic colours. Absent means `stage`.
   */
  tone?: 'stage' | 'success' | 'info' | 'warning' | 'danger' | 'muted';
  deepLink?: string;
}
export interface Breakdown {
  key: string;
  label: string;
  segments: BreakdownSegment[];
  /** What the bar deliberately leaves out, said in one quiet line under it. */
  caption?: { key: string; label: string; value: number } | null;
}

/** One day of the running week (Zeit). */
export interface WeekDay {
  date: string;
  workedMinutes: number;
  /** Null when the person has no work-time model: unknowable, not zero. */
  targetMinutes: number | null;
  today: boolean;
}

/**
 * The running week against what it owes (Zeit, #403).
 *
 * A null `targetMinutes` is the honest "nobody agreed a number with this
 * person" — no work-time model and no contract hours mirrored from People. The
 * card then draws the days without a bar rather than inventing a denominator.
 */
export interface WeekProgress {
  weekStart: string;
  workedMinutes: number;
  targetMinutes: number | null;
  /**
   * The working-time account balance (worked minus owed) as of the end of the
   * last completed week; `date` is a UTC calendar day. Null without a work-time
   * model or before the first completed week.
   */
  balanceAsOf: { date: string; balanceMinutes: number } | null;
  /**
   * Trust-based working time (Vertrauensarbeitszeit, issue 981): she records her
   * hours but no Soll is held against her, so `targetMinutes` and `balanceAsOf`
   * are null for a week she is trust-based throughout.
   */
  trustBased: boolean;
  /**
   * Her contract frees her from booking working time (issue 981): only project
   * time is recorded, a clock-in is refused. She has no target, no balance and no
   * working-time days; `projects` is her week.
   */
  projectOnly: boolean;
  /** This week's booked project time, largest first, minutes > 0 — hers to show only when `projectOnly`. */
  projects: WeekProject[];
  days: WeekDay[];
}

export interface WeekProject {
  projectId: string;
  name: string;
  /** The project's own colour as time-api holds it; not rendered (the module's categorical palette is). */
  color: string | null;
  minutes: number;
}

/**
 * The caller's earliest future date in this module — the "Heute" row's one
 * entry per module (workspace-start M1). Absent when there is none: never
 * null, never a placeholder. Type only here; M2 owns the workspace UI that
 * reads it.
 */
export interface UpcomingItem {
  /** Stable id, e.g. `leave:<id>`, `holiday:<yyyy-mm-dd>`, `interview:<id>`, `exam:<id>`, `station:<id>`. */
  key: string;
  /** `station`: a trainee moves to her next plan station (apprentice-api, ADR 0097). */
  kind: 'interview' | 'leave' | 'holiday' | 'exam' | 'station';
  /** ISO date/time. */
  at: string;
  /** ISO date/time, multi-day items only. */
  endAt?: string;
  allDay?: boolean;
  /** Data the caller renders verbatim — never a translated sentence. */
  label: string;
  deepLink: string;
}

export interface ModuleDashboard {
  module: string;
  keyFacts: KeyFact[];
  recents: RecentItem[];
  quickActions: QuickAction[];
  // `project_running` (issue 691): a person freed from working-time booking runs
  // a project timer with no block open — project time, no clock-in to end.
  clock?: { state: 'clocked_in' | 'clocked_out' | 'project_running'; since: string | null };
  // Optional extra "news" list (Apprentice trainer: submitted reports to review
  // + open overdue/blocked notifications). Reuses the RecentItem shape.
  news?: RecentItem[];
  // Optional stacked bar (ATS pipeline, Azubi report books).
  breakdown?: Breakdown | null;
  // Zeit only: the running week against its Sollstunden.
  weekProgress?: WeekProgress | null;
  upcoming?: UpcomingItem;
}

// user-api long module key → its fan-out client.
const DASHBOARD_CLIENTS: Record<string, { client: typeof atsApi }> = {
  ats: { client: atsApi },
  time_tracking: { client: timeApi },
  apprenticeship: { client: apprenticeApi },
};

export function moduleHasDashboard(moduleKey: string): boolean {
  return moduleKey in DASHBOARD_CLIENTS;
}

// Some modules send a bare fact/action key (Azubi: "waiting", not
// "apprentice.waiting") because their envelope's `module` string does not match
// the catalogue namespace (`apprenticeship` vs `apprentice`). A dotted key is
// already namespaced (ATS, Zeit) and is returned as is; a bare one is prefixed
// with the namespace the envelope's `module` maps to (workspace-start M2). The
// module's own keys are never renamed — this is workspace-side lookup only.
export function normalizeFactKey(module: string, key: string): string {
  if (key.includes('.')) return key;
  const ns = module === 'apprenticeship' ? 'apprentice' : module === 'timecard' ? 'time' : module;
  return `${ns}.${key}`;
}

// Localize a key-fact / quick-action label. The module returns a stable machine
// `key` (e.g. "ats.open_tasks") plus an English `label`. Known keys resolve via
// the workspace's own i18n (`homebase.facts.<module>.<name>`); anything the
// workspace doesn't recognise — a module shipping a new fact ahead of us — falls
// back to the module-provided label (forward-compatible, matches hide-on-failure).
// NOTE: `recents[].label` is data (task titles, names) and is NEVER passed here.
type Translate = (key: string) => string;
type TranslateExists = (key: string) => boolean;
export function localizedLabel(
  item: { key: string; label: string },
  t: Translate,
  te: TranslateExists,
  module?: string,
): string {
  const key = module ? normalizeFactKey(module, item.key) : item.key;
  const path = `homebase.facts.${key}`;
  return te(path) ? t(path) : item.label;
}

// One module's homebase contribution, with the two "nothing to render" cases kept
// apart (#364 F12): a module that ANSWERED (even with an empty dashboard) is not
// the same as one that could not be read at all — unreachable, or refused for this
// member. The panel says different things about them, so the fetch has to
// distinguish them; collapsing both into `null` is what let a refusal render as an
// empty workspace.
export type ModuleDashboardResult =
  | { status: 'ok'; dashboard: ModuleDashboard }
  | { status: 'unavailable' };

export async function loadModuleDashboard(moduleKey: string): Promise<ModuleDashboardResult> {
  const entry = DASHBOARD_CLIENTS[moduleKey];
  if (!entry) return { status: 'unavailable' };
  try {
    const { data } = await entry.client.get('/user-dashboard', {
      suppressGlobalError: true,
    });
    return { status: 'ok', dashboard: data as ModuleDashboard };
  } catch {
    return { status: 'unavailable' };
  }
}

// Best-effort fetch of one module's homebase contribution. Any failure (module
// not reachable, no data, missing permission) resolves to null so the card
// degrades to a plain launcher tile — the homebase never breaks.
export async function fetchModuleDashboard(moduleKey: string): Promise<ModuleDashboard | null> {
  const result = await loadModuleDashboard(moduleKey);
  return result.status === 'ok' ? result.dashboard : null;
}
